<script setup lang="ts">
import {
  isHighRarity,
  RARITY_COLORS,
  RARITY_LABELS,
  rarityTier,
} from '~/utils/apostatudo/roulette-rarity';
import type {
  RoulettePopup,
  RoulettePopupAction,
  RoulettePrize,
} from '@/types/roulette';

type Props = {
  popup: RoulettePopup | null;
  // Prêmio sorteado (na vitória) — fonte da raridade da fita e do glow. Null na
  // derrota (aí o modal não mostra fita e o glow fica neutro).
  prize?: RoulettePrize | null;
  // Nome canônico do prêmio (prize_name, top-level no resultado do giro) —
  // exibido em destaque (uppercase, 2 linhas) na vitória; null na derrota.
  prizeName?: string | null;
  // Imagem do prêmio sorteado já com fallback pra fatia (resolvida no
  // RouletteGame); tem precedência sobre a imagem do popup.
  prizeImageUrl?: string | null;
  // Giros restantes na roleta (já atualizado pelo spinsRemaining do giro). Com 0,
  // o botão de fechar vira "Voltar" em vez de "Girar de novo" (não há o que girar).
  spinsLeft?: number;
};

const {
  popup,
  prize = null,
  prizeName = null,
  prizeImageUrl = null,
  spinsLeft = 0,
} = defineProps<Props>();

// Sem giros restantes, "Girar de novo" não faz sentido — o botão só volta pra
// roda. Vira "Voltar" (com seta) tanto na vitória quanto na derrota.
const canSpinAgain = computed(() => spinsLeft > 0);

const emit = defineEmits<{ closeAll: [] }>();

const isOpen = defineModel<boolean>('modelValue', { default: false });

// Vitória = tem nome de prêmio (a derrota vem sem prizeName).
const isWin = computed(() => !!prizeName);

// Raridade do prêmio sorteado (só na vitória): pinta a fita e o glow.
const tier = computed(() => (prize ? rarityTier(prize) : null));
const tierLabel = computed(() => (tier.value ? RARITY_LABELS[tier.value] : ''));

// CSS vars da raridade pra fita (cor + glow).
const rarityVars = computed(() => {
  if (!tier.value) return {};

  const { color, glow } = RARITY_COLORS[tier.value];

  return { '--rar-color': color, '--rar-glow': glow };
});

// Glow radial atrás da imagem: cor do tier na vitória; neutro na derrota.
const glowStyle = computed(() => {
  const glow = tier.value ? RARITY_COLORS[tier.value].glow : 'transparent';
  const color = glow === 'transparent' ? '#334155' : glow;

  return {
    background: `radial-gradient(circle, ${color} 0%, transparent 68%)`,
  };
});

// Imagem central: prêmio sorteado (com fallback de fatia) > imagem do popup.
const imageUrl = computed(() => prizeImageUrl || popup?.imageUrl || '');

// Heading: título do core; na vitória cai no texto do Figma se o core não mandar.
const headingText = computed(() =>
  isWin.value ? popup?.title || 'Parabéns, você ganhou:' : popup?.title || '',
);

// CTA do core (o botão direito): opt-in por prêmio (close_only desliga). Usa o
// primaryAction; cai no secondaryAction se só ele existir.
const cta = computed(() =>
  popup && !popup.closeOnly
    ? (popup.primaryAction ?? popup.secondaryAction ?? null)
    : null,
);

// Cor do botão de CTA vem do core (action.color); sem valor, cai no azul padrão.
const ctaStyle = computed(() =>
  cta.value?.color
    ? { background: cta.value.color, borderColor: cta.value.color }
    : {},
);

// ── Comemoração (Lottie) ─────────────────────────────────────────────────────
// Estoura sobre o card quando o popup abre numa vitória de prêmio RARO ou acima
// (Raro/Épico/Supremo/Lendário) — prêmio comum e derrota não comemoram, pra o
// confete continuar significando "peguei algo bom". Player e animação vêm por
// import dinâmico: o lottie-web (~250kB) fica fora do chunk do modal e é buscado
// quando o sheet monta — muito antes de qualquer giro terminar.
const shouldCelebrate = computed(() => isWin.value && isHighRarity(tier.value));
const lottiePlayer = shallowRef<Component | null>(null);
const celebrationData = shallowRef<object | null>(null);

// A animação tem 100 frames a 60fps (~1.67s); em speed 0.8 dá ~2.08s. O timer
// desmonta em 2100ms, logo depois do último frame — e o fade-out do CSS termina
// exatamente aí (ver .roulette-popup__celebrate), então o frame 100 nunca fica
// congelado na tela: a opacidade já chegou a zero enquanto ele ainda se movia.
const CELEBRATION_SPEED = 0.8;
const CELEBRATION_MS = 2100;

const celebrating = ref(false);
let celebrateTimer: ReturnType<typeof setTimeout> | null = null;

function stopCelebration() {
  if (celebrateTimer) {
    clearTimeout(celebrateTimer);
    celebrateTimer = null;
  }

  celebrating.value = false;
}

// O v-if remonta o player, e é ele que reinicia a animação do frame 0 — daí o
// ciclo false → nextTick → true, que garante o reinício em vitórias seguidas.
async function celebrate() {
  if (!lottiePlayer.value || !celebrationData.value) return;

  stopCelebration();
  await nextTick();

  celebrating.value = true;
  celebrateTimer = setTimeout(stopCelebration, CELEBRATION_MS);
}

onMounted(async () => {
  try {
    const [player, data] = await Promise.all([
      import('vue3-lottie'),
      import('@/assets/json/lottie/success_celebration.json'),
    ]);

    lottiePlayer.value = player.Vue3Lottie;
    celebrationData.value = data.default;
  } catch {
    // Camada puramente decorativa: sem o chunk, o popup abre igual.
  }
});

onBeforeUnmount(stopCelebration);

// Dispara na abertura do popup quando o prêmio merece (ver shouldCelebrate).
// Também cobre o caso do chunk chegar depois (raro): o watch do player refaz a
// checagem.
watch([isOpen, lottiePlayer], ([opened]) => {
  if (opened && shouldCelebrate.value) celebrate();
  else if (!opened) stopCelebration();
});

function close() {
  isOpen.value = false;
}

// CTA do core: precisa fechar a roletinha INTEIRA (não só o popup) — senão o
// sheet, teleportado no body, fica montado por cima da rota nova numa navegação
// interna (SPA). URL absoluta navega externo; "/caminho" segue o roteamento
// interno com prefixo de locale.
function onAction(action: RoulettePopupAction) {
  if (!action.link) {
    close();

    return;
  }

  emit('closeAll');

  if (/^https?:\/\//i.test(action.link)) {
    navigateTo(action.link, { external: true });
  } else {
    navigateTo(localeLink(action.link));
  }
}
</script>

<template>
  <!-- Camada interna do modal da roleta (renderizada dentro do .rlt-screen):
       backdrop próprio + card centrado. -->
  <Transition name="rlt-popup">
    <div
      v-if="isOpen && popup"
      class="roulette-popup-layer"
      @click.self="close"
      @dragstart.prevent
    >
      <div
        class="roulette-popup"
        :style="rarityVars"
      >
        <!-- Fita da raridade (só na vitória): trapézio recolorível pelo tier. -->
        <div
          v-if="tierLabel"
          class="roulette-popup__ribbon"
        >
          <svg
            aria-hidden="true"
            class="roulette-popup__ribbon-shape"
            preserveAspectRatio="xMidYMid meet"
            viewBox="27 -10 174.5 37.5"
          >
            <path
              d="M197.651 -9.98486C201.162 -9.98486 202.97 -5.78317 200.554 -3.23383L172.969 25.8824C172.214 26.6797 171.164 27.1313 170.066 27.1313H60.2615C59.1632 27.1313 58.1132 26.6797 57.3578 25.8824L29.7722 -3.23378C27.3569 -5.78311 29.1641 -9.98486 32.6759 -9.98486H197.651Z"
              fill="var(--rar-color, #ff3700)"
            />
          </svg>
          <span class="roulette-popup__ribbon-label">{{ tierLabel }}</span>
        </div>

        <!-- Imagem com o glow da raridade atrás. -->
        <div class="roulette-popup__media">
          <div
            class="roulette-popup__glow"
            :style="glowStyle"
          />
          <img
            v-if="imageUrl"
            :alt="popup.title"
            class="roulette-popup__image"
            :src="imageUrl"
          />
        </div>

        <h2
          v-if="headingText"
          class="roulette-popup__title"
        >
          {{ headingText }}
        </h2>

        <!-- Vitória: nome do prêmio em uppercase, clampado em 2 linhas.
             Derrota: descrição do lose_popup. -->
        <p
          v-if="isWin"
          class="roulette-popup__prize"
        >
          {{ prizeName }}
        </p>
        <p
          v-else-if="popup.description"
          class="roulette-popup__description"
        >
          {{ popup.description }}
        </p>

        <!-- Botões: "Girar de novo" (fecha o popup, volta pra roda) + CTA do
             core quando existir. Sem CTA, sobra só o "Girar de novo". -->
        <div class="roulette-popup__actions">
          <button
            class="roulette-popup__btn roulette-popup__btn--again"
            type="button"
            @click="close"
          >
            <template v-if="canSpinAgain">
              Girar de novo
              <svg
                aria-hidden="true"
                class="roulette-popup__btn-icon"
                fill="none"
                viewBox="0 0 16 16"
              >
                <path
                  d="M13.4 8a5.4 5.4 0 1 1-1.55-3.8"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-width="1.6"
                />
                <path
                  d="M13.7 2.6v2.5h-2.5"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.6"
                />
              </svg>
            </template>
            <template v-else>
              <svg
                aria-hidden="true"
                class="roulette-popup__btn-icon"
                fill="none"
                viewBox="0 0 16 16"
              >
                <path
                  d="M13 8H4m4-4-4 4 4 4"
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="1.6"
                />
              </svg>
              Voltar
            </template>
          </button>

          <button
            v-if="cta"
            class="roulette-popup__btn roulette-popup__btn--cta"
            :style="ctaStyle"
            type="button"
            @click="onAction(cta)"
          >
            {{ cta.text }}
            <svg
              aria-hidden="true"
              class="roulette-popup__btn-icon"
              fill="none"
              viewBox="0 0 16 16"
            >
              <path
                d="M3 8h9m-3-4 4 4-4 4"
                stroke="currentColor"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="1.6"
              />
            </svg>
          </button>
        </div>

        <!-- Comemoração da vitória: último filho do card, então pinta por cima
             de tudo (fita, imagem, botões). pointer-events-none mantém os
             botões clicáveis durante a animação. -->
        <div
          v-if="celebrating"
          class="roulette-popup__celebrate"
          :style="{ '--celebrate-ms': `${CELEBRATION_MS}ms` }"
        >
          <component
            :is="lottiePlayer"
            :animation-data="celebrationData"
            :auto-play="true"
            class="roulette-popup__celebrate-anim"
            :loop="false"
            :speed="CELEBRATION_SPEED"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>

<style scoped lang="scss">
// Backdrop interno: cobre só o sheet da roleta (o pai .rlt-screen tem
// overflow-hidden, então respeita os cantos arredondados). O z vem da escala
// de camadas declarada no .rlt-screen (RouletteGame).
.roulette-popup-layer {
  @apply absolute inset-0 flex items-center justify-center select-none;

  z-index: var(--rlt-z-popup, 70);

  background: rgba(0, 0, 0, 0.66);
  backdrop-filter: blur(3px);
}

.roulette-popup {
  // Card no padrão dos cards de prêmio: gradiente navy + borda azulada.
  @apply relative flex flex-col items-center text-center;

  width: 86%;
  max-width: 300px;
  padding: 34px 18px 18px;
  border-radius: 12px;
  border: 1px solid #203570;
  background: linear-gradient(180deg, #08112d 0%, #111827 100%);
  box-shadow: 0 18px 44px rgba(0, 0, 0, 0.55);

  img {
    -webkit-user-drag: none;
  }
}

// Fita: trapézio (SVG recolorível) transbordando um pouco o topo do card, com o
// selo da raridade por cima.
.roulette-popup__ribbon {
  @apply absolute left-1/2 flex items-center justify-center;

  top: -11px;
  width: 56%;
  aspect-ratio: 174.5 / 37.5;
  transform: translateX(-50%);
  filter: drop-shadow(
    0 2px 10px color-mix(in srgb, var(--rar-glow, transparent) 45%, transparent)
  );
}

.roulette-popup__ribbon-shape {
  @apply absolute inset-0 w-full h-full;
}

.roulette-popup__ribbon-label {
  @apply relative font-extrabold uppercase text-black leading-none;

  // Sobe um tico: a base do trapézio afunila, o texto assenta melhor no topo.
  margin-bottom: 4px;
  font-size: 12px;
  letter-spacing: 0.3px;
}

// Imagem + glow da raridade atrás.
.roulette-popup__media {
  @apply relative flex items-center justify-center;

  width: 128px;
  height: 128px;
  margin-bottom: 12px;
}

.roulette-popup__glow {
  @apply absolute pointer-events-none;

  left: 50%;
  top: 50%;
  width: 150%;
  aspect-ratio: 1;
  transform: translate(-50%, -50%);
  filter: blur(24px);
  opacity: 0.5;
}

.roulette-popup__image {
  @apply relative w-full h-full object-contain;

  border-radius: 8px;
}

.roulette-popup__title {
  @apply text-texts text-base font-bold;
}

// Nome do prêmio em destaque: uppercase, clampado em 2 linhas com reticências.
.roulette-popup__prize {
  @apply text-texts font-extrabold uppercase leading-tight;

  margin-top: 4px;
  font-size: 15px;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
}

.roulette-popup__description {
  @apply text-texts/80 text-xs;

  margin-top: 4px;
}

// Linha dos botões: lado a lado, cada um com texto + ícone.
.roulette-popup__actions {
  @apply flex gap-1.5 w-full;

  margin-top: 18px;
}

.roulette-popup__btn {
  @apply flex items-center justify-center gap-1.5 rounded-lg font-semibold text-white whitespace-nowrap;

  height: 38px;
  padding: 0 10px;
  font-size: 12px;
  border: 1px solid transparent;
}

.roulette-popup__btn-icon {
  @apply shrink-0;

  width: 15px;
  height: 15px;
}

// Ambos os botões partem de colunas iguais (flex: 1 1 0). Com whitespace-nowrap,
// o min-width auto (min-content = largura do texto) faz o "Girar de novo" só
// passar dos 50% quando o texto não couber na metade — aí ele come o espaço do
// CTA. Quando cabe, fica 50/50.

// "Girar de novo": azul-escuro (default-hover do design).
.roulette-popup__btn--again {
  flex: 1 1 0;
  background: #12244d;
}

// CTA do core: azul (blue-500 do design). action.color sobrescreve o fundo via
// :style.
.roulette-popup__btn--cta {
  flex: 1 1 0;
  background: #1414dd;
}

// Comemoração: estoura a partir do card e transborda as bordas (o card não tem
// overflow-hidden; o corte fica no .rlt-screen, no limite do sheet). A animação
// é quadrada (300x300) e o preserveAspectRatio padrão (`meet`) centra o estouro
// sem distorcer — daí o inset negativo simétrico em vez de esticar.
.roulette-popup__celebrate {
  @apply absolute pointer-events-none select-none;

  z-index: 3;

  inset: -22%;
  // Um único ciclo de vida (entrada + saída) com a MESMA duração do timer que
  // desmonta o elemento — não dá pra usar o leave do <Transition> aqui, porque
  // ele só começaria depois do lottie parar, dissolvendo um frame congelado.
  animation: rlt-celebrate-life var(--celebrate-ms, 2100ms) linear forwards;
}

.roulette-popup__celebrate-anim {
  @apply absolute inset-0;
}

// Sobe rápido, segura, e apaga no rabo da animação: a queda de opacidade começa
// em 60% (~1.26s) e chega a zero junto com o último frame do lottie, então a
// saída acontece com os confetes AINDA em movimento.
@keyframes rlt-celebrate-life {
  0% {
    opacity: 0;
  }
  6% {
    opacity: 1;
  }
  60% {
    opacity: 1;
    animation-timing-function: ease-in;
  }
  100% {
    opacity: 0;
  }
}

// Pop-in do card (via @keyframes por causa do reset global de transition).
.rlt-popup-enter-active {
  animation: rlt-popup-in 0.22s cubic-bezier(0.22, 0.61, 0.36, 1);

  .roulette-popup {
    animation: rlt-popup-card-in 0.22s cubic-bezier(0.22, 0.61, 0.36, 1);
  }
}

.rlt-popup-leave-active {
  animation: rlt-popup-in 0.16s ease-in reverse;
}

@keyframes rlt-popup-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes rlt-popup-card-in {
  from {
    transform: scale(0.92);
  }
  to {
    transform: scale(1);
  }
}
</style>
